import React from 'react';
import { Button } from 'antd';
import { DollarOutlined, EyeOutlined } from '@ant-design/icons';
import {
  getIssuerDisplayName,
  getCouponDisplay,
  getMaturityDisplay,
  getRatingDisplay,
  getSecurityTypeBadge,
  isGovtSecurity,
} from './portfolioBondDisplay';
import './PortfolioBondCardList.css';

function BondCard({
  record,
  onViewDetails,
  onAddToPortfolio,
}) {
  const govt = isGovtSecurity(record);
  const issuer = (getIssuerDisplayName(record) || '').trim() || 'Unknown issuer';
  const coupon = getCouponDisplay(record);
  const maturity = getMaturityDisplay(record);
  const rating = getRatingDisplay(record);
  const typeBadge = getSecurityTypeBadge(record);

  return (
    <article className={`pb-card${govt ? ' pb-card--govt' : ''}`}>
      <div className="pb-card__main">
        <div className="pb-card__identity">
          <div className="pb-card__badges">
            <span className="pb-card__type-badge">{typeBadge}</span>
            <span className={`pb-card__rating-badge${rating === '—' ? ' pb-card__rating-badge--muted' : ''}`}>
              {rating}
            </span>
          </div>
          <h3 className="pb-card__issuer" title={issuer}>{issuer}</h3>
          <p className="pb-card__isin">
            <span className="pb-card__isin-label">ISIN</span>
            <code className="pb-card__isin-code">{record.isin || '—'}</code>
          </p>
        </div>

        <div className="pb-card__metrics">
          <div className="pb-card__metric pb-card__metric--highlight">
            <span className="pb-card__metric-label">Coupon</span>
            <span className="pb-card__metric-value">{coupon}</span>
          </div>
          <div className="pb-card__metric">
            <span className="pb-card__metric-label">Maturity</span>
            <span className="pb-card__metric-value">{maturity}</span>
          </div>
        </div>
      </div>

      <div className="pb-card__actions">
        {!govt && (
          <Button
            size="small"
            icon={<EyeOutlined />}
            className="pb-card__btn pb-card__btn--ghost"
            onClick={() => onViewDetails?.(record)}
          >
            View
          </Button>
        )}
        <Button
          type="primary"
          size="small"
          icon={<DollarOutlined />}
          className="pb-card__btn pb-card__btn--primary isin-detail-btn-watchlist"
          onClick={() => onAddToPortfolio?.(record)}
          title="Add to Portfolio"
        >
          Add
        </Button>
      </div>
    </article>
  );
}

function LoadingSkeleton() {
  return (
    <div className="pb-card pb-card--loading" aria-hidden>
      <div className="pb-card__main">
        <div className="pb-card__identity">
          <div className="pb-skeleton pb-skeleton--sm" />
          <div className="pb-skeleton pb-skeleton--lg" />
          <div className="pb-skeleton pb-skeleton--md" />
        </div>
        <div className="pb-card__metrics">
          <div className="pb-skeleton pb-skeleton--metric" />
          <div className="pb-skeleton pb-skeleton--metric" />
        </div>
      </div>
      <div className="pb-card__actions">
        <div className="pb-skeleton pb-skeleton--btn" />
        <div className="pb-skeleton pb-skeleton--btn pb-skeleton--btn-wide" />
      </div>
    </div>
  );
}

export default function PortfolioBondCardList({
  rows = [],
  loading = false,
  hasSearched = false,
  onClearAll,
  onViewDetails,
  onAddToPortfolio,
}) {
  if (loading) {
    return (
      <div className="pb-list" aria-busy="true" aria-label="Loading bonds">
        {Array.from({ length: 6 }).map((_, i) => (
          <LoadingSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="pb-empty">
        <span className="pb-empty__icon" aria-hidden>🔍</span>
        <h3 className="pb-empty__title">
          {hasSearched ? 'No results found' : 'No bonds in this universe'}
        </h3>
        <p className="pb-empty__hint">
          {hasSearched
            ? 'Try adjusting your filters or clearing them to start over.'
            : 'Bonds will appear here once data is loaded for the selected category.'}
        </p>
        {hasSearched && onClearAll && (
          <button type="button" className="hr-btn-apply" onClick={onClearAll}>
            Clear Filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="pb-list" role="list" aria-label="Bond results">
      {rows.map((record, index) => (
        <BondCard
          key={record.isin || index}
          record={record}
          onViewDetails={onViewDetails}
          onAddToPortfolio={onAddToPortfolio}
        />
      ))}
    </div>
  );
}
